import type { ProfileBooking } from './profileBooking.types';
import type { ProfileStackedMembership } from './profileMembership.types';
import type { ProfileLinkedAccount } from './ProfileLinkedAccounts';
import type { ProfileTransaction } from './profileAccount.types';

export type ProfileCloseResult = {
  success: boolean;
  redirect: boolean;
  redirectTo?: string;
};

export type ProfilePaymentMethod = {
  active?: boolean;
  type_id?: string;
  staff_only?: boolean;
};

export type ProfileRuntimeUser = {
  _id?: string;
  parent_id?: string | null;
  type?: string;
  name?: string;
  first_name?: string | null;
  last_name?: string | null;
  email?: string | null;
  phone?: string | null;
  emergency_contact?: string | null;
  image_url?: string | null;
  branch_id?: string;
  address?: {
    street?: string | null;
    country?: string | null;
    state?: string | null;
    city?: string | null;
    country_code?: string | null;
    postal_code?: string | null;
  } | null;
  metadata?: { fiscal?: { tax_id?: string | null } | null } | null;
  membership?: {
    type?: string;
    membership_name?: string;
    user_membership_id?: string | null;
    expiry_date?: number | null;
    subscription?: { end_date?: number | null; auto_renewal?: boolean } | null;
  } | null;
  mandate?: { _id?: string; bank_name?: string; account_number_ending?: string } | null;
  card?: { brand?: string; last4?: string } | null;
  consent?: unknown;
};

export type ProfileRuntimeBranch = {
  _id?: string;
  name?: string;
  address?: { timezone_id?: string };
  configuration?: {
    formats?: { date?: string; time?: string } | null;
    fiscal?: { member_tax_id_options?: { enabled?: boolean } | null } | null;
  } | null;
  paymentMethods?: ProfilePaymentMethod[];
  features?: {
    family_accounts?: { active?: boolean };
    booking?: {
      auto_booking_enabled?: boolean;
      booking_cancel_window?: number;
      late_cancellation_enabled?: boolean;
      penalty_settings?: Array<{ type?: string; amount?: number; enabled?: boolean }> | null;
    } | null;
  } | null;
};

export type RuntimeMembership = ProfileStackedMembership & {
  membershipMetaData?: NonNullable<ProfileStackedMembership['membershipMetaData']> & {
    isGroupMembership?: boolean;
    upfrontFee?: unknown;
    price?: unknown;
    duration?: { value?: number; timeUnit?: string };
  };
};

export type ConsentPurpose = {
  status?: boolean;
  default_status?: boolean;
  member_text?: string;
};

export type ConsentChannel = {
  key: string;
} & Record<string, ConsentPurpose | string | undefined>;

export function asRecord(value: unknown): Record<string, unknown> | null {
  if (value == null) {
    return null;
  }
  if (typeof value !== 'object') {
    return null;
  }
  if (Array.isArray(value)) {
    return null;
  }
  return value as Record<string, unknown>;
}

export function cloneUser(user: ProfileRuntimeUser): ProfileRuntimeUser {
  return structuredClone(user);
}

export function readBookings(response: unknown): { bookings: ProfileBooking[]; hasMore: boolean } {
  const body = asRecord(response);
  const bookings = Array.isArray(body?.data) ? (body.data as ProfileBooking[]) : [];
  return { bookings, hasMore: body?.has_more === true };
}

export function readCredits(response: unknown): { total: number; credits: Array<Record<string, unknown>> } {
  const body = asRecord(response);
  const total = typeof body?.total_value === 'number' ? body.total_value : 0;
  const credits = Array.isArray(body?.data) ? (body.data as Array<Record<string, unknown>>) : [];
  return { total, credits };
}

export function readTransactions(response: unknown): {
  rows: ProfileTransaction[];
  total: number;
  hasMore: boolean;
} {
  const body = asRecord(response);
  return {
    rows: Array.isArray(body?.data) ? (body.data as ProfileTransaction[]) : [],
    total: typeof body?.total_count === 'number' ? body.total_count : 0,
    hasMore: body?.has_more === true,
  };
}

export function readMemberships(response: unknown, cancelIds: string[]): RuntimeMembership[] {
  if (!Array.isArray(response)) {
    return [];
  }
  return (response as RuntimeMembership[])
    .filter((membership) => membership.membershipMetaData)
    .map((membership) => ({
      ...membership,
      isCancellationRequested: membership.id ? cancelIds.includes(membership.id) : false,
    }));
}

export function readCancelIds(value: unknown): string[] {
  if (!Array.isArray(value)) {
    return [];
  }
  return value.filter((id): id is string => typeof id === 'string');
}

export function readConsentChannels(
  response: unknown,
  hydrate: (channel: unknown) => void
): ConsentChannel[] {
  const consent = asRecord(asRecord(response)?.consent);
  if (!consent) {
    return [];
  }
  const channels: ConsentChannel[] = [];
  Object.keys(consent).forEach((channel) => {
    const purposes = asRecord(asRecord(consent[channel])?.purposes);
    if (!purposes) {
      return;
    }
    const consentObj = { key: channel, ...purposes } as ConsentChannel;
    hydrate(consentObj);
    channels.push(consentObj);
  });
  return channels;
}

function familyAccountsActive(branch: ProfileRuntimeBranch | null): boolean {
  return branch?.features?.family_accounts?.active === true;
}

function isMemberUser(user: ProfileRuntimeUser | null): boolean {
  return (user?.type ?? '').toLowerCase() === 'member';
}

function hasParentAccount(user: ProfileRuntimeUser): boolean {
  if (user.parent_id === undefined) {
    return false;
  }
  return user.parent_id !== null;
}

const inactiveFamily = { active: false, isChild: false, isGuard: false };

export function familyRole(user: ProfileRuntimeUser | null, branch: ProfileRuntimeBranch | null): {
  active: boolean;
  isChild: boolean;
  isGuard: boolean;
} {
  if (!familyAccountsActive(branch)) {
    return inactiveFamily;
  }
  if (!user) {
    return inactiveFamily;
  }
  if (!isMemberUser(user)) {
    return inactiveFamily;
  }
  const isChild = hasParentAccount(user);
  return { active: true, isChild, isGuard: !isChild };
}

export function branchAccepts(branch: ProfileRuntimeBranch | null, typeId: string): boolean {
  const match = (branch?.paymentMethods ?? []).find((method) => {
    return method.active && (method.type_id ?? '').toLowerCase() === typeId;
  });
  return !!match && !match.staff_only;
}

function filledObject(value: unknown): boolean {
  return !!value && typeof value === 'object' && Object.keys(value as object).length > 0;
}

export function memberHasCard(profile: ProfileRuntimeUser | null): boolean {
  return filledObject(profile?.card);
}

export function memberHasMandate(profile: ProfileRuntimeUser | null): boolean {
  return filledObject(profile?.mandate);
}

/** Port of `the profile dialog hasSubscriptionMembership` (`_.isEmpty(subscription)`). */
export function hasSubscriptionMembership(user: ProfileRuntimeUser | null): boolean {
  const subscription = user?.membership?.subscription;
  if (subscription == null) {
    return true;
  }
  return Object.keys(subscription).length === 0;
}

export function savedCardLabel(profile: ProfileRuntimeUser | null): string {
  if (!memberHasCard(profile) || !profile?.card) {
    return '';
  }
  return `${profile.card.brand ?? ''} ***${profile.card.last4 ?? ''}`;
}

export function savedMandateLabel(profile: ProfileRuntimeUser | null): string {
  if (!memberHasMandate(profile) || !profile?.mandate) {
    return '';
  }
  return `${profile.mandate.bank_name ?? ''} ***${profile.mandate.account_number_ending ?? ''}`;
}

export function buildMailTo(
  studioEmail: string,
  userName: string,
  membershipName: string,
  studioName: string
): string {
  const subject = 'Membership Inquiry';
  const body = [
    `Dear ${studioName} Team,`,
    '',
    `My name is ${userName}, and I am reaching out with an inquiry about my current membership plan, titled "${membershipName}".`,
    '',
    '[Please describe your inquiry here. For example, if you wish to cancel, pause your membership, or need general information about your membership benefits, please specify your request clearly.]',
    '',
    'Thank you for your assistance.',
    '',
    'Best regards,',
    `${userName}.`,
  ].join('\r\n');
  return `mailto:${encodeURIComponent(studioEmail)}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
}

export function readLinkedAccounts(response: unknown): ProfileLinkedAccount[] {
  return Array.isArray(response) ? (response as ProfileLinkedAccount[]) : [];
}

export function readDocumentList<T>(response: unknown): T[] | null {
  return Array.isArray(response) && response.length > 0 ? (response as T[]) : null;
}

export function asUser(value: unknown): ProfileRuntimeUser | null {
  const record = asRecord(value);
  return record ? (record as ProfileRuntimeUser) : null;
}

export function asBranch(value: unknown): ProfileRuntimeBranch | null {
  const record = asRecord(value);
  return record ? (record as ProfileRuntimeBranch) : null;
}
